const primaryFont = '"Libre Franklin","Helvetica Neue","Arial Nova", sans-serif';
const secondaryFont = '"Fira Sans","Helvetica Neue","Arial Nova", sans-serif';

export const fontFamily = {
  primaryFont,
  secondaryFont,
} as const;

export const fontSize = {
  textXs: '14px',
  textSm: '16px',
  textMd: '18px',
  textLg: '20px',
  h1: '44px',
  h2: '35px',
  h3: '28px',
  h4: '22px',
  h5: '18px',
  h6: '16px',
} as const;

export const lineHeight = {
  zero: '0',
  textXs: '135%',
  textSm: '135%',
  textMd: '135%',
  textLg: '135%',
  textXl: '143%',
  textXxl: '150%',
  h6: '120%',
  h5: '120%',
  h4: '120%',
  h3: '120%',
  h2: '120%',
  h1: '120%',
} as const;

export const letterSpacing = {
  neg2: '-0.02em',
  0: '0em',
} as const;

export const fontWeight = {
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
} as const;

export const font = {
  text: {
    textXs: {
      fontFamily: primaryFont,
      fontSize: fontSize.textXs,
      lineHeight: lineHeight.textXs,
      font() {
        return `${this.fontSize}/${this.lineHeight} ${this.fontFamily}`;
      },
    },
    textSm: {
      fontFamily: primaryFont,
      fontSize: fontSize.textSm,
      lineHeight: lineHeight.textSm,
      font() {
        return `${this.fontSize}/${this.lineHeight} ${this.fontFamily}`;
      },
    },
    textMd: {
      fontFamily: primaryFont,
      fontSize: fontSize.textMd,
      lineHeight: lineHeight.textMd,
      font() {
        return `${this.fontSize}/${this.lineHeight} ${this.fontFamily}`;
      },
    },
    textLg: {
      fontFamily: primaryFont,
      fontSize: fontSize.textLg,
      lineHeight: lineHeight.textLg,
      font() {
        return `${this.fontSize}/${this.lineHeight} ${this.fontFamily}`;
      },
    },
  },
  display: {
    h6: {
      fontFamily: secondaryFont,
      fontSize: fontSize.h6,
      lineHeight: lineHeight.h6,
      fontWeight: fontWeight.bold,
      letterSpacing: letterSpacing[0],
      font() {
        return `${this.fontWeight} ${this.fontSize}/${this.lineHeight} ${this.fontFamily}`;
      },
    },
    h5: {
      fontFamily: secondaryFont,
      fontSize: fontSize.h5,
      lineHeight: lineHeight.h5,
      fontWeight: fontWeight.bold,
      letterSpacing: letterSpacing[0],
      font() {
        return `${this.fontWeight} ${this.fontSize}/${this.lineHeight} ${this.fontFamily}`;
      },
    },
    h4: {
      fontFamily: secondaryFont,
      fontSize: fontSize.h4,
      lineHeight: lineHeight.h4,
      fontWeight: fontWeight.bold,
      letterSpacing: letterSpacing.neg2,
      font() {
        return `${this.fontWeight} ${this.fontSize}/${this.lineHeight} ${this.fontFamily}`;
      },
    },
    h3: {
      fontFamily: secondaryFont,
      fontSize: fontSize.h3,
      lineHeight: lineHeight.h3,
      fontWeight: fontWeight.bold,
      letterSpacing: letterSpacing.neg2,
      font() {
        return `${this.fontWeight} ${this.fontSize}/${this.lineHeight} ${this.fontFamily}`;
      },
    },
    h2: {
      fontFamily: secondaryFont,
      fontSize: fontSize.h2,
      lineHeight: lineHeight.h2,
      fontWeight: fontWeight.bold,
      letterSpacing: letterSpacing.neg2,
      font() {
        return `${this.fontWeight} ${this.fontSize}/${this.lineHeight} ${this.fontFamily}`;
      },
    },
    h1: {
      fontFamily: secondaryFont,
      fontSize: fontSize.h1,
      lineHeight: lineHeight.h1,
      fontWeight: fontWeight.bold,
      letterSpacing: letterSpacing.neg2,
      font() {
        return `${this.fontWeight} ${this.fontSize}/${this.lineHeight} ${this.fontFamily}`;
      },
    },
  },
} as const;

export type FontWeightDefinition = keyof typeof fontWeight;
export type FontSizeDefinition = keyof typeof fontSize;
export type FontFamilyDefinition = keyof typeof fontFamily;
export type LineHeightDefinition = keyof typeof lineHeight;
export type LetterSpacingDefinition = keyof typeof letterSpacing;
